import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ModalService } from '@app/shared/services/modal/modal.service';
import { DatePickerComponent } from '@app/shared/components/date-picker/date-picker.component';
import { ErrorSummaryComponent } from '../error-summary/error-summary.component';
import { ErrorMessage } from '../models/error-message.model';
import { IdentificationResult } from '../models/identification-result.model';
import { IdentificationService } from '../shared/identification.service';
import { ViewModelService } from '../shared/view-model.service';

@Component({
   selector: 'app-stepthree',
   standalone: true,
   imports: [DatePickerComponent, ErrorSummaryComponent, FormsModule, ModalComponent],
   templateUrl: './stepthree.component.html',
   styleUrl: './stepthree.component.scss',
})
export class StepthreeComponent implements OnInit, AfterViewInit {
   tooManyAttemptsTrigger = false;
   insufficientQuestions = false;
   disableSubmit = false;
   questions: string[] = [];
   lockedUntil: string | null = null;

   @ViewChild('informationModal') informationModal?: ModalComponent;
   @ViewChild('startDateModal') startDateModal?: ModalComponent;
   @ViewChild('rentChargeModal') rentChargeModal?: ModalComponent;

   public vm = inject(ViewModelService);
   private idvService = inject(IdentificationService);
   private router = inject(Router);
   public modalService = inject(ModalService);

   ngOnInit(): void {
      this.loadData();

      // Default values set up so validation works
      if (this.vm.answers.rentCharge == null) {
         this.vm.answers.rentCharge = '';
      }

      if (this.vm.answers.rentFrequency == null) {
         this.vm.answers.rentFrequency = 'null';
      }

      if (this.vm.answers.nationalInsuranceNumber == null) {
         this.vm.answers.nationalInsuranceNumber = '';
      }
   }

   ngAfterViewInit(): void {
      setTimeout(() => {
         if (this.vm.isReady) {
            this.showInformationMessage();
         } else {
            this.vm.contentLoaded.subscribe(() => {
               this.showInformationMessage();
            });
         }
      });
   }

   showInformationMessage(): void {
      if (this.vm.content && this.vm.content.step3InformationMessageBody !== '') {
         this.modalService.open('informationMessageModal', {
            backdrop: 'static',
            keyboard: false,
         });
      }
   }

   private loadData(): void {
      this.idvService.getQuestions().subscribe((questions) => {
         console.log('[IDV] questions received:', questions);
         if (questions != null && questions.length >= 2) {
            this.questions = questions;
            this.insufficientQuestions = false;
            this.vm.error = null;
            console.log('questions loaded');

            if ((window as any).ga) {
               (window as any).ga('send', 'event', {
                  eventCategory: 'ID&V',
                  eventLabel: 'Step Three',
                  eventAction: 'Questions Loaded',
                  eventValue: this.questions.length,
               });
            }
         } else {
            if ((window as any).ga) {
               (window as any).ga('send', 'event', {
                  eventCategory: 'ID&V',
                  eventLabel: 'Step Three',
                  eventAction: 'Insufficient Questions',
               });
            }
            this.insufficientQuestions = true;
            if (this.vm.content) {
               this.vm.error = new ErrorMessage(
                  this.vm.content.step3InsufficientQuestionsErrorTitle,
                  this.vm.content.step3InsufficientQuestionsErrorDescription
               );
            }
         }
      });
   }

   showQuestion(question: string): boolean {
      return this.questions.indexOf(question) >= 0;
   }

   submitForm(form: NgForm): void {
      if (this.tooManyAttemptsTrigger || this.insufficientQuestions) {
         return;
      }


      if ((window as any).ga) {
         (window as any).ga('send', 'event', {
            eventCategory: 'ID&V',
            eventLabel: 'Step Three',
            eventAction: 'Answers Submitted',
         });
      }

      this.disableSubmit = true;
      this.idvService.postAnswers().subscribe({
         next: (data) => this.success(data),
         error: (error) => this.fail(error),
      });
   }

   success(data: IdentificationResult): void {
      if (!data.success) {
         if ((window as any).ga) {
            (window as any).ga('send', 'event', {
               eventCategory: 'ID&V',
               eventLabel: 'Step Three',
               eventAction: 'Failed ID&V',
            });
         }

         if (data.isLocked) {
            this.tooManyAttemptsTrigger = true;
            if (data.lockExpiresAt) {
               const expiresAt = new Date(data.lockExpiresAt);
               const isToday = expiresAt.toDateString() === new Date().toDateString();
               this.lockedUntil = isToday
                  ? expiresAt.toLocaleTimeString(undefined, { timeStyle: 'short' })
                  : expiresAt.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
            } else {
               this.lockedUntil = null;
            }
            if (this.vm.content) {
               const lockedUntilSuffix = this.lockedUntil ? ` Your account is locked until ${this.lockedUntil}.` : '';
               this.vm.error = new ErrorMessage(
                  this.vm.content.step3TooManyAttemptsErrorTitle,
                  this.vm.content.step3TooManyAttemptsErrorDescription + lockedUntilSuffix
               );
            }
         } else {
            if (this.vm.content) {
               this.vm.error = new ErrorMessage(
                  this.vm.content.step3InvalidSubmissionErrorTitle,
                  this.vm.content.step3InvalidSubmissionErrorDescription
               );
            }
            this.disableSubmit = false;
         }
         return;
      }

      if ((window as any).ga) {
         (window as any).ga('send', 'event', {
            eventCategory: 'ID&V',
            eventLabel: 'Step Three',
            eventAction: 'Successful ID&V',
         });
      }

      this.vm.resumeUrl = data.resumeUrl;
      this.vm.loginUrl = data.loginUrl;
      this.router.navigate(['stepthree-confirm']);
   }

   fail(error: any): void {
      if (this.vm.content) {
         this.vm.error = new ErrorMessage(
            this.vm.content.stepAbortUnknownErrorTitle,
            this.vm.content.stepAbortUnknownErrorMessage
         );
      }
      this.disableSubmit = false;
   }

   showStartDateModal(): void {
      this.modalService.open('startDateQuestionModal', { backdrop: true, keyboard: true });
   }

   showRentChargeModal(): void {
      this.modalService.open('rentChargeQuestionModal', { backdrop: true, keyboard: true });
   }

   goBack(): void {
      this.router.navigate(['steptwo']);
   }

   formatNationalInsurance(event: Event): void {
      const input = event.target as HTMLInputElement;
      let value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      this.vm.answers.nationalInsuranceNumber = value;
   }
}
